import { useState, useEffect, useRef } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Server, ClipboardList, Box, Search, Layers, ChevronRight } from 'lucide-react'
import api from '../utils/api'

interface NavItem {
  path: string
  label: string
  icon: any
  shortcut?: string
  action?: () => void
}

interface SearchResultItem {
  id: string
  type: 'equipment' | 'rack' | 'work_order' | 'cable'
  title: string
  subtitle?: string
  datacenter_code: string
  url: string
  metadata?: Record<string, any>
}

interface CommandPaletteProps {
  onClose: () => void
  items: NavItem[]
}

export function CommandPalette({ onClose, items }: CommandPaletteProps) {
  const [query, setQuery] = useState('')
  const [selectedIndex, setSelectedIndex] = useState(0)
  const [localResults, setLocalResults] = useState<SearchResultItem[]>([])
  const [externalResults, setExternalResults] = useState<SearchResultItem[]>([])
  const [isSearching, setIsSearching] = useState(false)
  const [showExternal, setShowExternal] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)
  const navigate = useNavigate()
  const { dcCode = 'dc-alpha' } = useParams<{ dcCode: string }>()

  // Filter static navigation items
  const navFiltered = items.filter(i =>
    i.label.toLowerCase().includes(query.toLowerCase())
  )

  // Combined flat list of selectable items
  type FlatItem =
    | { kind: 'nav'; item: NavItem }
    | { kind: 'local'; item: SearchResultItem }
    | { kind: 'external'; item: SearchResultItem }

  const flatItems: FlatItem[] = [
    ...navFiltered.map(i => ({ kind: 'nav' as const, item: i })),
    ...localResults.map(i => ({ kind: 'local' as const, item: i })),
    ...(showExternal ? externalResults.map(i => ({ kind: 'external' as const, item: i })) : []),
  ]

  // Debounced API search
  useEffect(() => {
    if (query.trim().length < 2) {
      setLocalResults([])
      setExternalResults([])
      setIsSearching(false)
      return
    }

    const timer = setTimeout(async () => {
      setIsSearching(true)
      try {
        const resp = await api.get(`/api/datacenters/${dcCode}/search?q=${encodeURIComponent(query.trim())}`)
        setLocalResults(resp.data.local_results || [])
        setExternalResults(resp.data.external_results || [])
      } catch (err) {
        console.error('Failed to search assets:', err)
      } finally {
        setIsSearching(false)
      }
    }, 200)

    return () => clearTimeout(timer)
  }, [query, dcCode])

  useEffect(() => {
    setSelectedIndex(0)
  }, [query, showExternal, localResults.length])

  useEffect(() => {
    inputRef.current?.focus()
  }, [])

  const handleSelect = (flat: FlatItem) => {
    if (flat.kind === 'nav') {
      if (flat.item.action) {
        flat.item.action()
      } else {
        navigate(flat.item.path)
      }
    } else {
      navigate(flat.item.url)
    }
    onClose()
  }

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault()
        onClose()
      }
      if (e.key === 'ArrowDown') {
        e.preventDefault()
        setSelectedIndex(i => Math.min(i + 1, flatItems.length - 1))
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault()
        setSelectedIndex(i => Math.max(i - 1, 0))
      }
      if (e.key === 'Enter' && flatItems[selectedIndex]) {
        e.preventDefault()
        handleSelect(flatItems[selectedIndex])
      }
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [flatItems, selectedIndex, onClose])

  const renderIcon = (type?: string, defaultIcon?: any) => {
    switch (type) {
      case 'equipment':
        return <Server className="w-4 h-4 text-accent" />
      case 'rack':
        return <Box className="w-4 h-4 text-blue-400" />
      case 'work_order':
        return <ClipboardList className="w-4 h-4 text-amber-400" />
      default:
        if (defaultIcon) {
          const IconComp = defaultIcon
          return <IconComp className="w-4 h-4" />
        }
        return <Layers className="w-4 h-4 text-text-muted" />
    }
  }

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[200] flex items-start justify-center pt-[15vh] p-4 animate-fade-in" onClick={onClose}>
      <div
        className="w-full max-w-2xl bg-bg-panel border border-border/80 rounded-2xl shadow-2xl overflow-hidden flex flex-col max-h-[70vh]"
        onClick={e => e.stopPropagation()}
      >
        {/* Search input */}
        <div className="flex items-center gap-3 px-5 py-4 border-b border-border/60 bg-bg-panel">
          <Search className="w-5 h-5 text-text-muted shrink-0" />
          <input
            ref={inputRef}
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder="Поиск по нарядам, оборудованию, стойкам и командам (Ctrl+K)..."
            className="flex-1 bg-transparent outline-none text-base text-text-primary placeholder:text-text-muted"
          />
          {isSearching && (
            <div className="w-4 h-4 border-2 border-accent border-t-transparent rounded-full animate-spin shrink-0" />
          )}
          <kbd className="text-[10px] font-mono text-text-muted border border-border/60 rounded px-1.5 py-0.5 bg-bg-input">ESC</kbd>
        </div>

        {/* Results scroll area */}
        <div className="flex-1 overflow-y-auto p-2 space-y-4">
          {flatItems.length === 0 && !isSearching && (
            <div className="px-4 py-12 text-center text-text-muted text-sm">
              <Search className="w-8 h-8 mx-auto mb-2 opacity-40" />
              Ничего не найдено по запросу «{query}»
            </div>
          )}

          {/* Navigation group */}
          {navFiltered.length > 0 && (
            <div>
              <p className="text-[10px] font-bold text-text-muted uppercase tracking-wider px-3 py-1 mb-1">
                Разделы и действия
              </p>
              <div className="space-y-1">
                {navFiltered.map(item => {
                  const globalIdx = flatItems.findIndex(f => f.kind === 'nav' && f.item === item)
                  const isSelected = globalIdx === selectedIndex
                  return (
                    <button
                      key={item.path}
                      onClick={() => handleSelect({ kind: 'nav', item })}
                      className={`w-full flex items-center gap-3 px-3 py-2 rounded-xl text-sm transition-all ${
                        isSelected
                          ? 'bg-accent text-white shadow-sm'
                          : 'text-text-secondary hover:text-text-primary hover:bg-bg-hover'
                      }`}
                    >
                      <span className="shrink-0">{renderIcon(undefined, item.icon)}</span>
                      <span className="flex-1 text-left font-medium">{item.label}</span>
                      {item.shortcut && (
                        <kbd className={`text-[10px] px-1.5 py-0.5 rounded border ${isSelected ? 'border-white/30 text-white' : 'border-border text-text-muted'}`}>
                          {item.shortcut}
                        </kbd>
                      )}
                    </button>
                  )
                })}
              </div>
            </div>
          )}

          {/* Active Datacenter Assets */}
          {localResults.length > 0 && (
            <div>
              <p className="text-[10px] font-bold text-accent uppercase tracking-wider px-3 py-1 mb-1 flex items-center justify-between">
                <span>{dcCode?.toUpperCase()} · Найдено в активном ЦОД</span>
                <span className="text-[10px] font-normal">{localResults.length}</span>
              </p>
              <div className="space-y-1">
                {localResults.map(item => {
                  const globalIdx = flatItems.findIndex(f => f.kind === 'local' && f.item === item)
                  const isSelected = globalIdx === selectedIndex
                  return (
                    <button
                      key={item.id}
                      onClick={() => handleSelect({ kind: 'local', item })}
                      className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm transition-all ${
                        isSelected
                          ? 'bg-accent text-white shadow-sm'
                          : 'text-text-secondary hover:text-text-primary hover:bg-bg-hover'
                      }`}
                    >
                      <span className="shrink-0">{renderIcon(item.type)}</span>
                      <div className="flex-1 text-left min-w-0">
                        <p className={`font-semibold truncate ${isSelected ? 'text-white' : 'text-text-primary'}`}>{item.title}</p>
                        {item.subtitle && (
                          <p className={`text-xs truncate ${isSelected ? 'text-white/80' : 'text-text-muted'}`}>{item.subtitle}</p>
                        )}
                      </div>
                      <ChevronRight className={`w-4 h-4 shrink-0 ${isSelected ? 'text-white' : 'text-text-muted'}`} />
                    </button>
                  )
                })}
              </div>
            </div>
          )}

          {/* External Datacenter Assets (collapsible) */}
          {externalResults.length > 0 && (
            <div className="border-t border-border/40 pt-2">
              <button
                onClick={() => setShowExternal(!showExternal)}
                className="w-full flex items-center justify-between px-3 py-1.5 text-[11px] font-bold text-text-muted uppercase tracking-wider hover:text-text-primary transition-colors"
              >
                <span>Найдено в других ЦОД ({externalResults.length})</span>
                <span className="text-xs text-accent font-semibold">{showExternal ? 'Свернуть' : 'Развернуть'}</span>
              </button>
              {showExternal && (
                <div className="space-y-1 mt-1">
                  {externalResults.map(item => {
                    const globalIdx = flatItems.findIndex(f => f.kind === 'external' && f.item === item)
                    const isSelected = globalIdx === selectedIndex
                    return (
                      <button
                        key={item.id}
                        onClick={() => handleSelect({ kind: 'external', item })}
                        className={`w-full flex items-center gap-3 px-3 py-2 rounded-xl text-sm transition-all ${
                          isSelected
                            ? 'bg-accent text-white shadow-sm'
                            : 'text-text-secondary hover:text-text-primary hover:bg-bg-hover'
                        }`}
                      >
                        <span className="shrink-0">{renderIcon(item.type)}</span>
                        <div className="flex-1 text-left min-w-0">
                          <div className="flex items-center gap-2">
                            <span className={`font-semibold truncate ${isSelected ? 'text-white' : 'text-text-primary'}`}>{item.title}</span>
                            <span className="text-[10px] px-1.5 py-0.2 rounded bg-bg-input text-text-muted border border-border/60">
                              {item.datacenter_code}
                            </span>
                          </div>
                          {item.subtitle && (
                            <p className={`text-xs truncate ${isSelected ? 'text-white/80' : 'text-text-muted'}`}>{item.subtitle}</p>
                          )}
                        </div>
                        <ChevronRight className={`w-4 h-4 shrink-0 ${isSelected ? 'text-white' : 'text-text-muted'}`} />
                      </button>
                    )
                  })}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer info */}
        <div className="px-5 py-2.5 border-t border-border/40 bg-bg-input/40 flex items-center justify-between text-[11px] text-text-muted">
          <div className="flex items-center gap-4">
            <span className="flex items-center gap-1"><kbd className="px-1 py-0.5 rounded bg-bg-panel border border-border text-[9px]">↑</kbd> <kbd className="px-1 py-0.5 rounded bg-bg-panel border border-border text-[9px]">↓</kbd> выбор</span>
            <span className="flex items-center gap-1"><kbd className="px-1 py-0.5 rounded bg-bg-panel border border-border text-[9px]">↵</kbd> переход</span>
          </div>
          <span>DC Cable Link Omnibar</span>
        </div>
      </div>
    </div>
  )
}
